"use client";

import { useEffect, useMemo, useState } from "react";

type MerchPriority = "high" | "medium" | "low";
type MerchCriterion = { name: string; score: number; finding: string };
type MerchRecommendation = { title: string; action: string; priority: MerchPriority; x: number; y: number };
type MerchResult = {
  id?: number;
  pharmacy: string;
  department: string;
  score: number;
  summary: string;
  criteria: MerchCriterion[];
  recommendations: MerchRecommendation[];
  createdBy?: string;
  createdAt?: number;
};
type MerchTask = {
  id: number;
  title: string;
  details: string;
  priority: "normal" | "important" | "urgent";
  pharmacies: string[];
  displayDate: string;
  status: "open" | "completed";
  completedBy: string;
};

const departments = ["Solaire", "Dermatologie", "Bébé", "Hygiène bucco-dentaire", "Compléments alimentaires", "Orthopédie", "Premiers soins", "Saison / animation"];
const departmentIcons: Record<string, string> = { Solaire: "☀", Dermatologie: "✦", Bébé: "♧", "Hygiène bucco-dentaire": "◇", "Compléments alimentaires": "◌", Orthopédie: "＋", "Premiers soins": "✚", "Saison / animation": "◎" };
const todayIso = () => new Date().toISOString().slice(0, 10);
const displayDate = (value: number) => new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

function normalizeAnalysis(row: Record<string, unknown>): MerchResult {
  const parse = <T,>(value: unknown, fallback: T): T => {
    try { return typeof value === "string" ? JSON.parse(value) as T : (value as T) ?? fallback; } catch { return fallback; }
  };
  return {
    id: Number(row.id),
    pharmacy: String(row.pharmacy ?? "Square"),
    department: String(row.department ?? "Rayon"),
    score: Number(row.score) || 0,
    summary: String(row.summary ?? ""),
    criteria: parse<MerchCriterion[]>(row.criteria, []),
    recommendations: parse<MerchRecommendation[]>(row.recommendations, []),
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? Date.now()),
  };
}

function normalizeTask(row: Record<string, unknown>): MerchTask {
  let pharmacies: string[] = [];
  try { pharmacies = JSON.parse(String(row.pharmacies ?? "[]")); } catch { pharmacies = []; }
  return {
    id: Number(row.id),
    title: String(row.title ?? ""),
    details: String(row.details ?? ""),
    priority: String(row.priority ?? "normal") as MerchTask["priority"],
    pharmacies,
    displayDate: String(row.displayDate ?? row.display_date ?? ""),
    status: String(row.status ?? "open") as MerchTask["status"],
    completedBy: String(row.completedBy ?? row.completed_by ?? ""),
  };
}

async function prepareImage(file: File) {
  const source = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Photo illisible"));
    reader.readAsDataURL(file);
  });
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error("Photo illisible"));
    element.src = source;
  });
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  canvas.getContext("2d")?.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", .82);
}

export function MerchandisingModule({ onBack, initialPharmacy, initialOperator }: { onBack: () => void; initialPharmacy: string; initialOperator: string }) {
  const [view, setView] = useState<"dashboard" | "analysis" | "tasks" | "history">("dashboard");
  const [pharmacy, setPharmacy] = useState(initialPharmacy || "Square");
  const [department, setDepartment] = useState("Solaire");
  const [photo, setPhoto] = useState("");
  const [result, setResult] = useState<MerchResult | null>(null);
  const [analyses, setAnalyses] = useState<MerchResult[]>([]);
  const [tasks, setTasks] = useState<MerchTask[]>([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingTasks, setSavingTasks] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = async () => {
    try {
      const [analysisResponse, taskResponse] = await Promise.all([
        fetch(`/api/merchandising-analyses?pharmacy=${encodeURIComponent(pharmacy)}`, { cache: "no-store" }),
        fetch("/api/daily-tasks", { cache: "no-store" }),
      ]);
      const analysisPayload = await analysisResponse.json();
      const taskPayload = await taskResponse.json();
      if (!analysisResponse.ok) throw new Error(analysisPayload.error);
      setAnalyses((analysisPayload.items ?? []).map(normalizeAnalysis));
      if (taskResponse.ok) setTasks((taskPayload.tasks ?? []).map(normalizeTask).filter((task: MerchTask) => task.title.startsWith("Merchandising ·") && task.pharmacies.includes(pharmacy)));
    } catch {
      setMessage("Le suivi merchandising est momentanément indisponible.");
    }
  };

  useEffect(() => { void loadData(); }, [pharmacy]);

  const departmentLatest = useMemo(() => departments.map((name) => analyses.find((analysis) => analysis.department === name)).filter(Boolean) as MerchResult[], [analyses]);
  const average = analyses.length ? Math.round(analyses.reduce((sum, item) => sum + item.score, 0) / analyses.length) : 0;
  const openTasks = tasks.filter((task) => task.status === "open");

  const selectPhoto = async (file?: File) => {
    if (!file) return;
    setMessage("Préparation de la photo…");
    try {
      setPhoto(await prepareImage(file));
      setResult(null);
      setMessage("Photo prête pour l’analyse.");
    } catch {
      setMessage("Cette photo n’a pas pu être lue.");
    }
  };

  const runAnalysis = async () => {
    if (!photo) { setMessage("Ajoutez d’abord une photo du rayon."); return; }
    setLoading(true);
    setMessage("");
    try {
      const response = await fetch("/api/merchandising-ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pharmacy, department, imageDataUrl: photo }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Analyse indisponible");
      const analysis: MerchResult = { ...payload, pharmacy, department };
      const saveResponse = await fetch("/api/merchandising-analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...analysis, createdBy: initialOperator }),
      });
      const saved = await saveResponse.json();
      if (!saveResponse.ok) throw new Error(saved.error || "Historique indisponible");
      const complete = normalizeAnalysis(saved);
      setResult(complete);
      setSelectedRecommendations(complete.recommendations.map((_, index) => index));
      setAnalyses((current) => [complete, ...current]);
      setMessage("Analyse terminée et ajoutée à l’historique partagé.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "L’analyse n’a pas abouti.");
    } finally {
      setLoading(false);
    }
  };

  const createTasks = async () => {
    if (!result || !selectedRecommendations.length) { setMessage("Sélectionnez au moins une recommandation."); return; }
    setSavingTasks(true);
    try {
      const chosen = result.recommendations.filter((_, index) => selectedRecommendations.includes(index));
      const responses = await Promise.all(chosen.map((recommendation) => fetch("/api/daily-tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "task",
          title: `Merchandising · ${recommendation.title}`,
          details: `${result.department} · ${recommendation.action}`,
          priority: recommendation.priority === "high" ? "important" : "normal",
          pharmacies: [pharmacy],
          createdBy: initialOperator,
          assignedTo: "Toute l’équipe",
          displayDate: todayIso(),
        }),
      })));
      if (responses.some((response) => !response.ok)) throw new Error("Certaines actions n’ont pas été créées.");
      await loadData();
      setView("tasks");
      setMessage(`${chosen.length} action${chosen.length > 1 ? "s" : ""} ajoutée${chosen.length > 1 ? "s" : ""} à la To-do du portail.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Les actions n’ont pas pu être créées.");
    } finally {
      setSavingTasks(false);
    }
  };

  const completeTask = async (task: MerchTask) => {
    const response = await fetch("/api/daily-tasks", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: task.id, action: task.status === "open" ? "complete" : "reopen", operator: initialOperator }) });
    if (response.ok) await loadData();
    else setMessage("Le statut de l’action n’a pas pu être modifié.");
  };

  const startAnalysis = (name = department) => { setDepartment(name); setView("analysis"); setResult(null); setPhoto(""); setMessage(""); };

  return (
    <main className="merch-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <span className="module-status"><i /> IA connectée</span>
      </header>

      <div className="merch-shell">
        <aside className="merch-sidebar">
          <div className="merch-brand"><span>✚</span><div><strong>PharmaMerch</strong><small>Merchandising officinal</small></div></div>
          <nav>{([['dashboard','⌂','Accueil'],['analysis','◉','Analyser'],['tasks','✓','Actions'],['history','↗','Historique']] as const).map(([id, icon, label]) => <button type="button" key={id} className={view === id ? "active" : ""} onClick={() => setView(id)}><span>{icon}</span>{label}</button>)}</nav>
          <label><span>Pharmacie active</span><select value={pharmacy} onChange={(event) => setPharmacy(event.target.value)}><option>Square</option><option>Ragadan</option><option>Laurentine</option></select></label>
        </aside>

        <section className="merch-content">
          <header className="merch-topbar"><div><span>ASSISTANT MERCHANDISING</span><h1>{view === "dashboard" ? "Vue d’ensemble" : view === "analysis" ? "Analyser un rayon" : view === "tasks" ? "Plan d’action" : "Historique des analyses"}</h1></div>{view !== "analysis" && <button type="button" onClick={() => startAnalysis()}>＋ Nouvelle analyse</button>}</header>
          {message && <div className="merch-message" role="status">{message}<button type="button" onClick={() => setMessage("")}>×</button></div>}

          {view === "dashboard" && <>
            <section className="merch-hero"><div><span>PILOTAGE OFFICINAL</span><h2>Des rayons plus lisibles, analysés sur une vraie photo.</h2><p>L’IA observe uniquement ce qui est visible et transforme ses recommandations en actions d’équipe.</p></div><button type="button" onClick={() => startAnalysis()}>Prendre une photo</button></section>
            <div className="merch-stats"><article><span>Note moyenne</span><strong>{average || "—"}{average ? "/100" : ""}</strong><small>{analyses.length ? `${analyses.length} analyse${analyses.length > 1 ? "s" : ""}` : "Première analyse à réaliser"}</small></article><article><span>Rayons suivis</span><strong>{departmentLatest.length}</strong><small>sur {departments.length} rayons proposés</small></article><article><span>Actions ouvertes</span><strong>{openTasks.length}</strong><small>dans la To-do du portail</small></article></div>
            <section className="merch-departments"><header><div><span>RAYONS SUIVIS</span><h2>Dernier diagnostic par rayon</h2></div></header><div>{departments.map((name) => { const latest = analyses.find((analysis) => analysis.department === name); return <button type="button" key={name} onClick={() => startAnalysis(name)}><i>{departmentIcons[name]}</i><span><strong>{name}</strong><small>{latest?.createdAt ? `Analysé le ${displayDate(latest.createdAt)}` : "À analyser"}</small></span><b className={latest && latest.score < 65 ? "warning" : ""}>{latest ? latest.score : "＋"}</b></button>; })}</div></section>
          </>}

          {view === "analysis" && <div className="merch-analysis-grid">
            <section className="merch-panel merch-upload"><header><div><span>NOUVELLE ANALYSE</span><h2>Photographier le rayon</h2></div><b>Mobile optimisé</b></header><label><span>Rayon</span><select value={department} onChange={(event) => setDepartment(event.target.value)}>{departments.map((name) => <option key={name}>{name}</option>)}</select></label><div className="merch-photo-tips"><span>Rayon de face</span><span>Bonne lumière</span><span>Meuble entier visible</span></div><label className={`merch-dropzone ${photo ? "has-photo" : ""}`}><input type="file" accept="image/*" capture="environment" onChange={(event) => void selectPhoto(event.target.files?.[0])} />{photo ? <img src={photo} alt="Rayon à analyser" /> : <span><b>▣</b><strong>Prendre ou ajouter une photo</strong><small>JPG, PNG ou appareil photo</small></span>}<em>{photo ? "Changer la photo" : ""}</em></label><button type="button" className="merch-run" onClick={() => void runAnalysis()} disabled={loading}>{loading ? "Analyse IA en cours…" : "✨ Analyser ce rayon"}</button><small className="merch-privacy">La photo sert à l’analyse et n’est pas conservée dans l’historique.</small></section>
            <section className="merch-panel merch-result">{!result ? <div className="merch-empty"><span>◎</span><h3>Le diagnostic apparaîtra ici</h3><p>Ajoutez une photo puis lancez l’analyse IA.</p></div> : <><header className="merch-result-head"><div><span>DIAGNOSTIC MERCHANDISING</span><h2>Rayon {result.department.toLocaleLowerCase("fr")}</h2><p>{result.summary}</p></div><b>{result.score}<small>/100</small></b></header><div className="merch-annotated"><img src={photo} alt="Rayon analysé" />{result.recommendations.slice(0, 5).map((recommendation, index) => <span key={`${recommendation.title}-${index}`} style={{ left: `${recommendation.x}%`, top: `${recommendation.y}%` }}>{index + 1}</span>)}</div><div className="merch-criteria">{result.criteria.map((criterion) => <article key={criterion.name}><div><strong>{criterion.name}</strong><span>{criterion.score}/100</span></div><i><b style={{ width: `${criterion.score}%` }} /></i><small>{criterion.finding}</small></article>)}</div><div className="merch-recommendations"><h3>Actions recommandées</h3>{result.recommendations.map((recommendation, index) => <label key={`${recommendation.title}-${index}`} className={recommendation.priority}><input type="checkbox" checked={selectedRecommendations.includes(index)} onChange={() => setSelectedRecommendations((current) => current.includes(index) ? current.filter((item) => item !== index) : [...current, index])} /><span><i>{index + 1}</i><strong>{recommendation.title}</strong><small>{recommendation.action}</small></span><b>{recommendation.priority === "high" ? "Prioritaire" : recommendation.priority === "medium" ? "Important" : "Amélioration"}</b></label>)}</div><button type="button" className="merch-create-tasks" onClick={() => void createTasks()} disabled={savingTasks}>{savingTasks ? "Création…" : `Créer ${selectedRecommendations.length || "les"} action${selectedRecommendations.length > 1 ? "s" : ""} dans la To-do`}</button></>}</section>
          </div>}

          {view === "tasks" && <section className="merch-list-card"><header><div><span>TO-DO DU PORTAIL</span><h2>Actions merchandising · {pharmacy}</h2><p>Ces tâches sont partagées avec l’équipe et visibles sur la page d’accueil.</p></div><b>{openTasks.length} ouverte{openTasks.length > 1 ? "s" : ""}</b></header><div>{tasks.map((task) => <article key={task.id} className={task.status}><button type="button" onClick={() => void completeTask(task)}>{task.status === "completed" ? "✓" : ""}</button><span><strong>{task.title.replace("Merchandising · ", "")}</strong><p>{task.details}</p><small>{task.displayDate} · {task.priority === "important" ? "Prioritaire" : "Normal"}{task.completedBy ? ` · Fait par ${task.completedBy}` : ""}</small></span></article>)}{!tasks.length && <div className="merch-empty-list">Aucune action merchandising pour cette pharmacie.</div>}</div></section>}

          {view === "history" && <section className="merch-history"><header><div><span>HISTORIQUE PARTAGÉ</span><h2>Progression de {pharmacy}</h2></div><button type="button" onClick={() => window.print()}>Imprimer / PDF</button></header><div className="merch-history-table"><table><thead><tr><th>Date</th><th>Rayon</th><th>Note</th><th>Diagnostic</th><th>Par</th></tr></thead><tbody>{analyses.map((analysis) => <tr key={analysis.id}><td>{displayDate(analysis.createdAt ?? Date.now())}</td><td><strong>{analysis.department}</strong></td><td><b className={analysis.score < 65 ? "warning" : ""}>{analysis.score}/100</b></td><td>{analysis.summary}</td><td>{analysis.createdBy}</td></tr>)}</tbody></table>{!analyses.length && <div className="merch-empty-list">La première analyse apparaîtra ici.</div>}</div></section>}
        </section>
      </div>
    </main>
  );
}
